import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  VStack,
  HStack,
  Badge,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { Mail, AlertCircle, CreditCard } from 'lucide-react'
import { toaster } from '@/shared/lib/toaster'
import type { OwnerBalance } from '../types'

interface SendPaymentLinkModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  unitGroup: {
    unitId: string
    unitNumber: string
    accountCode: string
    owners: OwnerBalance[]
    totalBalance: number
    hasMultipleOwners: boolean
  } | null
  selectedOwner: OwnerBalance | null
  onSubmit: (data: PaymentLinkFormData) => Promise<void>
}

export interface PaymentLinkFormData {
  ownerId: string
  ownerEmail: string
  amount: number
  description: string
  unitId: string
}

export function SendPaymentLinkModal({
  isOpen,
  onClose,
  unitGroup,
  selectedOwner,
  onSubmit,
}: SendPaymentLinkModalProps) {
  const [formData, setFormData] = useState<PaymentLinkFormData>({
    ownerId: '',
    ownerEmail: '',
    amount: 0,
    description: '',
    unitId: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Calculer le montant suggéré pour ce propriétaire
  const getSuggestedAmount = (owner: OwnerBalance, unitGroup: SendPaymentLinkModalProps['unitGroup']) => {
    if (!owner || !unitGroup) return 0

    // Si plusieurs propriétaires, calculer la part
    if (unitGroup.hasMultipleOwners && owner.share_numerator && owner.share_denominator) {
      // Utiliser le solde individuel du propriétaire s'il existe
      if (owner.balance !== undefined && owner.balance !== null) {
        return Math.max(0, owner.balance)
      }
      // Sinon calculer proportionnellement
      const share = owner.share_numerator / owner.share_denominator
      return Math.max(0, Math.round(unitGroup.totalBalance * share * 100) / 100)
    }

    // Sinon, utiliser le solde total
    return Math.max(0, unitGroup.totalBalance)
  }

  // Initialiser le formulaire
  useEffect(() => {
    if (isOpen && selectedOwner && unitGroup) {
      const suggestedAmount = getSuggestedAmount(selectedOwner, unitGroup)

      setFormData({
        ownerId: selectedOwner.owner_id || '',
        ownerEmail: '', // L'email n'est pas dans OwnerBalance, il faudra le récupérer
        amount: suggestedAmount,
        description: `Règlement copropriété - Lot ${unitGroup.unitNumber}`,
        unitId: unitGroup.unitId,
      })
    }
  }, [isOpen, selectedOwner, unitGroup])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const handleSubmit = async () => {
    // Validation
    if (!formData.ownerEmail) {
      toaster.create({
        title: 'Erreur',
        description: "Veuillez saisir l'adresse email",
        type: 'error',
      })
      return
    }

    if (!formData.amount || formData.amount <= 0) {
      toaster.create({
        title: 'Erreur',
        description: 'Le montant doit être supérieur à 0',
        type: 'error',
      })
      return
    }

    // Validation email simple
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.ownerEmail)) {
      toaster.create({
        title: 'Erreur',
        description: 'Adresse email invalide',
        type: 'error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      toaster.create({
        title: 'Succès',
        description: `Lien de paiement envoyé à ${formData.ownerEmail}`,
        type: 'success',
      })
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de l'envoi"
      toaster.create({ title: 'Erreur', description: message, type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!unitGroup || !selectedOwner) return null

  const suggestedAmount = getSuggestedAmount(selectedOwner, unitGroup)

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="lg">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="550px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              <HStack gap={2}>
                <CreditCard size={20} />
                <Text>Envoyer un lien de paiement</Text>
              </HStack>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={5} align="stretch">
              {/* Info copropriétaire */}
              <Box p={4} bg="gray.50" borderRadius="md">
                <VStack align="stretch" gap={2}>
                  <HStack justify="space-between">
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Destinataire
                      </Text>
                      <Text fontWeight="semibold">
                        {selectedOwner.first_name} {selectedOwner.last_name}
                      </Text>
                    </Box>
                    <Box textAlign="right">
                      <Text fontSize="sm" color="gray.600">
                        Lot {unitGroup.unitNumber}
                      </Text>
                      <Text fontSize="sm" fontFamily="mono" color="gray.500">
                        {unitGroup.accountCode}
                      </Text>
                    </Box>
                  </HStack>

                  {unitGroup.hasMultipleOwners && selectedOwner.share_numerator && (
                    <HStack gap={2}>
                      <Badge colorPalette="purple" variant="subtle">
                        Quote-part : {selectedOwner.share_numerator}/{selectedOwner.share_denominator}
                      </Badge>
                      <Text fontSize="sm" color="gray.600">
                        (Part individuelle : {formatCurrency(suggestedAmount)})
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </Box>

              {/* Solde */}
              {unitGroup.totalBalance > 0 && (
                <Box p={3} bg="red.50" borderRadius="md" borderLeft="4px solid" borderColor="red.500">
                  <HStack gap={2}>
                    <AlertCircle size={18} color="var(--chakra-colors-red-600)" />
                    <Text color="red.700" fontWeight="medium">
                      Solde débiteur du lot : {formatCurrency(unitGroup.totalBalance)}
                    </Text>
                  </HStack>
                </Box>
              )}

              {/* Formulaire */}
              <Field label="Adresse email" required>
                <HStack>
                  <Box color="gray.400">
                    <Mail size={18} />
                  </Box>
                  <Input
                    type="email"
                    value={formData.ownerEmail}
                    onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                    placeholder="email@exemple.com"
                    flex={1}
                  />
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Un lien de paiement Stripe sera envoyé à cette adresse
                </Text>
              </Field>

              <Flex gap={4}>
                <Box flex={1}>
                  <Field label="Montant à payer" required>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="0,00"
                    />
                    {suggestedAmount > 0 && formData.amount !== suggestedAmount && (
                      <Button
                        variant="ghost"
                        size="xs"
                        mt={1}
                        onClick={() => setFormData({ ...formData, amount: suggestedAmount })}
                      >
                        Utiliser le montant suggéré ({formatCurrency(suggestedAmount)})
                      </Button>
                    )}
                  </Field>
                </Box>
              </Flex>

              <Field label="Description (visible par le payeur)">
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Règlement appel de fonds T1 2025"
                />
              </Field>

              {/* Info Stripe */}
              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  Le copropriétaire recevra un email avec un lien sécurisé Stripe pour effectuer
                  son paiement par carte bancaire. Une fois le paiement reçu, vous serez notifié
                  et pourrez enregistrer le règlement.
                </Text>
              </Box>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!formData.amount || formData.amount <= 0 || !formData.ownerEmail}
            >
              <Mail size={16} />
              Envoyer le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
